/**
 * Utility module for URL-related functions
 *
 */

/**
 * Parameters that should be preserved when updating URL for search operations
 * These parameters maintain user context across search, filter, and sort operations
 */
const PRESERVED_SEARCH_PARAMS = ['q', 'sort_by', 'tab'];

/**
 * Get the current domain of the shop
 *
 * @returns {string} The current domain of the shop
 */
const getCurrentDomain = () => {
  // Get current shop url
  const url = window.location.hostname;

  // If Dev IP address, return the hostname, ie: 127.0.0.1
  if (!Number.isNaN(url.substring(0, 3))) {
    return url;
  }

  // Extracts domain only from current shop (i.e. removes 'www.' and leaves '.allbirds.com')
  // This step is necessary because there are some domains with TLDs that are multi-part (co.uk)
  let domain = url.substring(url.indexOf('.'));

  // if there's no subdomain in the current window location, prepend the domain with
  // a period to include any subdomains that are navigated to
  if (domain.charAt(0) !== '.') {
    domain = `.${domain}`;
  }

  return domain;
};

/**
 * Parse the query string into an object
 *
 * @param {string} queryString - The query string to parse
 * @returns {Object} The parsed query string as an object
 */
const parseQueryString = (queryString = window.location.search) => {
  const params = {};
  const searchParams = new URLSearchParams(queryString);

  for (const [key, value] of searchParams) {
    params[key] = value;
  }

  return params;
};

/**
 * Update the browser history with new query params
 *
 * @param {Object} options - The options object
 * @param {string} [options.pathname=window.location.pathname] - The pathname to use
 * @param {Object} options.searchParams - The object containing the new query params to add/update
 * @returns {void}
 */
const updateHistory = ({ pathname = window.location.pathname, searchParams = {} }) => {
  // Get the current search parameters
  const currentSearchParams = new URLSearchParams(window.location.search);

  // Set or update parameters from the input object
  for (const [key, value] of Object.entries(searchParams)) {
    if (value === null || value === undefined) {
      currentSearchParams.delete(key);
    } else {
      currentSearchParams.set(key, String(value));
    }
  }

  // Construct the new URL
  const newSearchString = currentSearchParams.toString();
  const newUrl = newSearchString ? `${pathname}?${newSearchString}` : pathname;

  // Update the browser history without reloading the page
  window.history.replaceState({}, '', newUrl);
};

/**
 * Remove given query param list from the current URL
 *
 * @param {Array} queryParamsToRemoveList - List of query params to remove
 * @returns {void}
 */
const removeQueryString = (queryParamsToRemoveList = []) => {
  const searchParams = {};

  queryParamsToRemoveList.forEach((queryParamToRemove) => {
    searchParams[queryParamToRemove] = null;
  });

  updateHistory({ searchParams });
};

/**
 * Check if a URL can be parsed
 * Fallback to new URL() if URL.canParse is not available
 *
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL can be parsed, false otherwise
 */
const canParse = (url) => {
  let result = false;

  // If URL.canParse is available, use it
  if (URL.canParse && typeof URL.canParse === 'function') {
    result = URL.canParse(url);
  } else {
    try {
      // If object can be created, it means the URL can be parsed
      new URL(url);
      result = true;
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      result = false;
    }
  }

  return result;
};

/**
 * Get the value of a query param from the current URL
 *
 * @param {string} queryParamKey - The query param key to get the value of
 * @param {string} [queryString=window.location.search] - The query string to parse
 *
 * @returns {string} The value of the query param
 */
const getQueryStringValue = (queryParamKey, queryString = window.location.search) => {
  const queryStringObject = parseQueryString(queryString);

  return queryStringObject[queryParamKey] || null;
};

/**
 * Clears price filter parameters from URL while preserving other parameters
 *
 * @returns {URL} The modified URL object with price filters removed
 */
const clearPriceFilter = () => {
  // Create a URL object from the current location
  const url = new URL(window.location.href);

  // Remove price filter parameters
  url.searchParams.delete('filter.v.price.gte');
  url.searchParams.delete('filter.v.price.lte');
  url.searchParams.delete('page');

  return url;
};

/**
 * Clears all filter parameters while optionally preserving specific parameters
 *
 * @param {Array<string>} preserveParams - Array of parameter names to preserve
 * @returns {URL} The modified URL object with filters removed
 */
const clearAllFilters = (preserveParams = []) => {
  const url = new URL(window.location.href);
  const preservedValues = {};

  // Save the parameters we want to preserve
  preserveParams.forEach((param) => {
    if (url.searchParams.has(param)) {
      preservedValues[param] = url.searchParams.get(param);
    }
  });

  // Clear all search parameters
  url.search = '';

  // Restore preserved parameters
  Object.entries(preservedValues).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url;
};

/**
 * Updates the browser history with a new URL
 *
 * @param {URL} url - The URL object to update the browser history with
 * @param {boolean} [useReplace=false] - Whether to use replaceState instead of pushState
 */
const updateUrlWithObject = (url, useReplace = false) => {
  if (useReplace) {
    window.history.replaceState({}, '', url.toString());
  } else {
    window.history.pushState({}, '', url.toString());
  }
};

/**
 * Updates URL parameters and resets pagination to page 1
 * This is the standard pattern for URL updates in search/collection pages
 *
 * @param {Object} params - Object containing parameters to update
 * @param {Object} options - Configuration options
 * @param {Array<string>} [options.preserveParams=PRESERVED_SEARCH_PARAMS] - Parameters to preserve from current URL
 * @param {boolean} [options.resetPage=true] - Whether to reset pagination to page 1
 * @returns {void}
 */
const updateUrlAndResetPagination = (params = {}, options = {}) => {
  const { preserveParams = PRESERVED_SEARCH_PARAMS, resetPage = true } = options;

  const currentParams = new URLSearchParams(window.location.search);
  const newParams = new URLSearchParams();

  // Preserve specified parameters from current URL
  preserveParams.forEach((param) => {
    if (currentParams.has(param) && !(param in params)) {
      newParams.set(param, currentParams.get(param));
    }
  });

  // Add/update new parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      newParams.delete(key);
    } else {
      newParams.set(key, String(value));
    }
  });

  // Reset pagination by default
  if (resetPage) {
    newParams.delete('page');
  }

  // Update URL
  const newUrl = newParams.toString()
    ? `${window.location.pathname}?${newParams.toString()}`
    : window.location.pathname;

  window.history.replaceState({}, '', newUrl);
};

export {
  canParse,
  clearAllFilters,
  clearPriceFilter,
  getCurrentDomain,
  getQueryStringValue,
  parseQueryString,
  removeQueryString,
  updateHistory,
  updateUrlAndResetPagination,
  updateUrlWithObject,
};
